import { modalStore } from '@/stores/modal.store';

type NotificationType = 'success' | 'warning' | 'danger';

interface Props {
    text: string;
    type?: NotificationType;
}

const TYPE_STYLE: Record<
    NotificationType,
    {
        accent: string;
        iconBg: string;
        chip: string;
        button: { bg: string; shadow: string };
        icon: JSX.Element;
    }
> = {
    success: {
        accent: '#6ee7b7',
        iconBg: 'rgba(110,231,183,0.14)',
        chip: 'border-accent_4/40 bg-accent_4/[.14] text-accent_4',
        button: {
            bg: 'linear-gradient(180deg, #3acdb1, #2bb89b)',
            shadow:
                '0 10px 24px rgba(43,184,155,0.40), inset 0 1px 0 rgba(255,255,255,0.30)',
        },
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-7 h-7"
            >
                <circle cx="12" cy="12" r="9" />
                <path d="M8 12l3 3 5-6" />
            </svg>
        ),
    },
    warning: {
        accent: '#ffb13d',
        iconBg: 'rgba(255,177,61,0.14)',
        chip: 'border-accent_5/40 bg-accent_5/[.14] text-accent_5',
        button: {
            bg: 'linear-gradient(180deg, #ffc664, #f29318)',
            shadow:
                '0 10px 24px rgba(242,147,24,0.35), inset 0 1px 0 rgba(255,255,255,0.30)',
        },
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-7 h-7"
            >
                <path d="M12 3l10 18H2z" />
                <path d="M12 10v5" />
                <circle cx="12" cy="18" r="0.6" fill="currentColor" />
            </svg>
        ),
    },
    danger: {
        accent: '#ff5454',
        iconBg: 'rgba(255,84,84,0.14)',
        chip: 'border-[rgba(255,84,84,0.40)] bg-[rgba(255,84,84,0.14)] text-danger',
        button: {
            bg: 'linear-gradient(180deg, #ff7d6e, #f04e3a)',
            shadow:
                '0 10px 24px rgba(240,78,58,0.35), inset 0 1px 0 rgba(255,255,255,0.25)',
        },
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-7 h-7"
            >
                <circle cx="12" cy="12" r="9" />
                <path d="M9 9l6 6M15 9l-6 6" />
            </svg>
        ),
    },
};

const Notification = ({ text, type = 'success' }: Props) => {
    const { toggleModal } = modalStore((state) => state);
    const style = TYPE_STYLE[type] ?? TYPE_STYLE.success;

    // Preserve \n line breaks and \n\n paragraph splits from backend payload
    const paragraphs = (text || '').split(/\n\n+/);

    return (
        <div className="flex flex-col items-center gap-4 w-[min(480px,90vw)] max-w-full">
            <div
                className="w-14 h-14 grid place-items-center rounded-full border"
                style={{
                    background: style.iconBg,
                    borderColor: `color-mix(in srgb, ${style.accent} 40%, transparent)`,
                    color: style.accent,
                    boxShadow: `0 0 24px -6px ${style.accent}, inset 0 0 18px -8px ${style.accent}`,
                }}
            >
                {style.icon}
            </div>

            <span
                className={`text-[10px] font-bold tracking-[0.16em] uppercase px-2.5 py-1 rounded-md border ${style.chip}`}
            >
                {type}
            </span>

            <div className="flex flex-col gap-3 text-text_2 text-[14px] leading-[1.6] text-center whitespace-pre-line break-words">
                {paragraphs.map((p, i) => (
                    <p key={i}>{p}</p>
                ))}
            </div>

            <button
                onClick={() => toggleModal(false)}
                className="mt-1 px-7 py-3 rounded-fl text-[13px] font-bold tracking-[0.06em] text-white hover:-translate-y-0.5 hover:brightness-110 transition-all"
                style={{
                    background: style.button.bg,
                    boxShadow: style.button.shadow,
                }}
            >
                CLOSE
            </button>
        </div>
    );
};

export default Notification;
